import React, { useState, useCallback } from 'react';
import { withStyles } from '@material-ui/core/styles';
import { Button, IconButton, SvgIcon, Tooltip } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import { useTranslation } from 'react-i18next';
import styles from './styles';

const ThickCloseIcon = (props) => (
  <SvgIcon {...props} viewBox="0 0 24 24">
    <path d="M18.5 6.91L17.09 5.5 12 10.59 6.91 5.5 5.5 6.91 10.59 12 5.5 17.09 6.91 18.5 12 13.41 17.09 18.5 18.5 17.09 13.41 12z" 
          stroke="currentColor"
          strokeWidth="1.5"
          fill="currentColor"
          strokeLinecap="round"
          strokeLinejoin="round" />
  </SvgIcon>
);

const LinkRow = React.memo(({ classes, onRemove, onChange, value, isLastRow }) => {
  const { t } = useTranslation();

  const handleChange = useCallback((e) => {
    onChange(e.target.value);
  }, [onChange]);

  return (
    <div className={classes.linkRow}>
      <input
        type="text"
        className={classes.linkInput}
        placeholder={t('actions.create.enterLink')}
        value={value}
        onChange={handleChange}
      />
      <Tooltip title={isLastRow ? t('actions.create.minLinksReached') : ""}>
        <span>
          <IconButton 
            onClick={onRemove} 
            className={`${classes.removeButton} ${isLastRow ? classes.removeButtonDisabled : ''}`}
            disabled={isLastRow}
          >
            <ThickCloseIcon />
          </IconButton>
        </span>
      </Tooltip>
    </div>
  );
});

const MAX_LINKS = 3;

const SupportingLinks = ({ classes, onChange, value = [] }) => {
  const { t } = useTranslation();
  const [links, setLinks] = useState(value.length > 0 ? value : ['']);
  const [isHovered, setIsHovered] = useState(false);

  const updateParent = useCallback((newLinks) => {
    if (typeof onChange === 'function') {
      onChange(newLinks.filter(link => link !== ''));
    }
  }, [onChange]);

  const addLink = useCallback(() => {
    if (links.length < MAX_LINKS) {
      const newLinks = [...links, ''];
      setLinks(newLinks);
      updateParent(newLinks);
    }
  }, [links, updateParent]);

  const removeLink = useCallback((index) => {
    const newLinks = links.filter((_, i) => i !== index);
    setLinks(newLinks);
    updateParent(newLinks);
  }, [links, updateParent]);

  const updateLink = useCallback((index, newValue) => {
    const newLinks = [...links];
    newLinks[index] = newValue;
    setLinks(newLinks);
    updateParent(newLinks);

    // Notify parent of the specific link being edited
    if (typeof onChange === 'function') {
      onChange(newLinks, index); // Pass the index to the parent
    }
  }, [links, updateParent, onChange]);

  const isMaxLinksReached = links.length >= MAX_LINKS;

  return (
    <div className={classes.supportingLinksContainer}>
      {links.map((link, index) => (
        <LinkRow
          key={index}
          classes={classes}
          onRemove={() => removeLink(index)}
          onChange={(newValue) => updateLink(index, newValue)}
          value={link}
          isLastRow={links.length === 1}
        />
      ))}
      <Tooltip title={isMaxLinksReached ? t('actions.create.maxLinksReached') : ''} placement="top">
        <span>
          <Button
            fullWidth
            startIcon={<AddIcon className={classes.addIcon} style={{ color: isHovered && !isMaxLinksReached ? 'white' : '' }} />}
            onClick={addLink}
            className={`${classes.addButton} ${isMaxLinksReached ? classes.addButtonDisabled : ''}`}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            disabled={isMaxLinksReached}
          >
            {t('actions.create.addLink')}
          </Button>
        </span>
      </Tooltip>
    </div>
  );
};

export default React.memo(withStyles(styles)(SupportingLinks));
